import { CHANNEL_LOG_TYPE_NAMES } from './channel-log-type-names';
import type { ActivityLog } from './types';

/**
 * Activity-log `type` wire values referenced in branching logic (object/relationship types in
 * their collapsed `_o:*:` form). The label/group tables below are still keyed by the raw strings.
 */
export const LOG_TYPE = {
  Event: 'event',
  Geofence: 'geofence',
  AttributeChange: 'attribute_change',
  FailedAttributeChange: 'failed_attribute_change',
  ObjectAttributeChange: '_o:*:attribute_change',
  ObjectFailedAttributeChange: '_o:*:failed_attribute_change',
  ProfileCreate: 'profile_create',
  ObjectCreate: '_o:*:create',
  ProfileDelete: 'profile_delete',
  ObjectDelete: '_o:*:delete',
  ProfileMerge: 'profile_merge',
  SegmentBuild: 'segment_build',
  SegmentMembershipChange: 'profile_segment_membership_change',
  WebhookEvent: 'webhook_event',
  AnonMerge: 'anon_merge',
  FailedLlmAction: 'failed_llm_action',
} as const;

/**
 * Human labels for activity-log `type` values, keyed by the parsed type (object/relationship
 * types are collapsed to their `_o:*:` / `_r:*:` form first). Channel-specific labels come from
 * `CHANNEL_LOG_TYPE_NAMES`; a channel-native type without an entry falls back to its raw value.
 */
const LOG_TYPE_NAMES: Record<string, string> = {
  anon_merge: 'Anonymous Data Merged',
  attempted_action: 'Attempted to Send Action',
  attribute_change: 'Attribute Change',
  bounced_action: 'Bounced Action',
  clicked_action: 'Clicked Action',
  clicked_content: 'Clicked Content',
  converted_action: 'Converted Action',
  converted_content: 'Converted Content',
  deferred_action: 'Deferred Action',
  delivered_action: 'Delivered Action',
  device_change: 'Device Change',
  drafted_action: 'Drafted Action',
  dropped_action: 'Suppressed Action',
  event: 'Event',
  failed_action: 'Failed to Send Action',
  failed_attribute_change: 'Failed Attribute Change',
  failed_batch_update: 'Failed Batch Update',
  failed_event: 'Failed Event',
  failed_object_journeys: 'Failed Journeys for Object/Relationship Campaign',
  failed_query_collection: 'Failed Collection Query',
  geofence: 'Geofence',
  '_o:*:attribute_change': 'Object Attribute Change',
  '_o:*:create': 'Object Created',
  '_o:*:delete': 'Object Deleted',
  '_o:*:failed_attribute_change': 'Object Failed Attribute Change',
  '_o:*:add_relationship': 'Relationship Created (to Person)',
  '_o:*:delete_relationship': 'Relationship Deleted (to Person)',
  opened_action: 'Opened Action',
  page: 'Page View',
  add_relationship: 'Relationship Created (to Object)',
  delete_relationship: 'Relationship Deleted (to Object)',
  profile_create: 'Person Created',
  profile_delete: 'Person Deleted',
  profile_merge: 'People Merged',
  '_r:*:attribute_change': 'Relationship Attribute Change',
  '_r:*:failed_attribute_change': 'Relationship Failed Attribute Change',
  screen: 'Screen View',
  profile_segment_membership_change: 'Segment Membership Change',
  segment_build: 'Build Segment',
  sent_action: 'Sent Action',
  skipped_update: 'Skipped Update',
  spammed_action: 'Spammed Action',
  replied_action: 'Replied to Action',
  undeliverable_action: 'Undeliverable Action',
  unsubscribed_action: 'Unsubscribed Action',
  viewed_content: 'Viewed Content',
  webhook_event: 'Webhook Arrived',
  inbound: 'Inbound Message',
  failed_llm_action: 'Failed LLM',
};

/** Product-noun terms the dynamic labels interpolate (resolved via the taxonomy service). */
export interface ActivityLogTypeTerms {
  campaignSingular: string;
  personSingular: string;
  personPlural: string;
}

/**
 * Delivery-type → channel display name, used to replace the word "Action" in `*_action` labels
 * (e.g. "Sent Action" → "Sent Email"). Covers the common channels; white-label / less-common
 * delivery types fall back to the generic "Action".
 */
const DELIVERY_TYPE_NAMES: Record<string, string> = {
  email: 'Email',
  push: 'Push Notification',
  sms: 'SMS',
  slack: 'Slack',
  webhook: 'Webhook',
  twilio: 'SMS',
  whatsapp: 'WhatsApp',
  line: 'LINE',
  in_app: 'In-App',
  letter: 'Letter',
};

/** Collapse object/relationship types to their wildcard form (`_o:<id>:x` → `_o:*:x`). */
export function parseLogType(type: string): string {
  return type.replace(/(_(o|r)):[^:]+:/, '$1:*:');
}

function resolveDeliveryTypeName(deliveryType: string | null | undefined): string {
  if (deliveryType == null || deliveryType === '') {
    return 'Action';
  }
  return DELIVERY_TYPE_NAMES[deliveryType] ?? 'Action';
}

/**
 * Human label for a log's type. Applies the taxonomy-dependent overrides, then swaps the word
 * "Action" for the channel-specific delivery name (e.g. "Sent Action" → "Sent Email"). Falls
 * back to the raw parsed type when unmapped.
 */
export function activityLogTypeLabel(log: ActivityLog, terms: ActivityLogTypeTerms): string {
  const parsed = parseLogType(log.type);

  const overrides: Record<string, string> = {
    failed_object_journeys: `Failed Journeys for Object/Relationship ${terms.campaignSingular}`,
    '_o:*:add_relationship': `Relationship Created (to ${terms.personSingular})`,
    '_o:*:delete_relationship': `Relationship Deleted (to ${terms.personSingular})`,
    profile_create: `${terms.personSingular} Created`,
    profile_delete: `${terms.personSingular} Deleted`,
    profile_merge: `${terms.personPlural} Merged`,
  };

  // Channel-native types (sent_sms, delivered_line, …) resolve from the channel map; their
  // labels already name the channel, so the "Action" swap below is a no-op for them.
  const name = overrides[parsed] ?? LOG_TYPE_NAMES[parsed] ?? CHANNEL_LOG_TYPE_NAMES[parsed];
  if (name == null) {
    return parsed;
  }
  return name.replace(/\bAction\b/, resolveDeliveryTypeName(log.delivery_type));
}

export interface ActivityTypeOption {
  value: string;
  label: string;
}

/** A collapsible option group for the Activity Type Select (Pluma grouped-options shape). */
export interface ActivityTypeOptionGroup {
  label: string;
  options: ActivityTypeOption[];
  isCollapsible: boolean;
  defaultIsCollapsed: boolean;
}

// Channel suffixes + action prefixes used to bucket `action_channel` types into dropdown groups.
const CHANNEL_NAMES = new Set([
  'email',
  'in_app',
  'inbox',
  'line',
  'push',
  'slack',
  'sms',
  'twilio',
  'urban_airship',
  'webhook',
  'whatsapp',
]);
const DELIVERY_ACTIONS = new Set(['attempted', 'sent', 'delivered', 'drafted', 'deferred']);
const ENGAGEMENT_ACTIONS = new Set(['clicked', 'opened', 'converted', 'replied']);
const SUPPRESSION_ACTIONS = new Set(['bounced', 'dropped', 'undeliverable', 'unsubscribed', 'failed']);

const GROUP_ORDER = [
  'Delivery Actions',
  'Engagement Actions',
  'Suppression Actions',
  'Profile & Identity',
  'Relationships',
  'Behavioral Events',
  'System Operations',
  'Other',
] as const;

/** Exact type → group. Built from per-group lists so the mapping reads as data, not branches. */
const GROUP_BY_EXACT_TYPE: Record<string, string> = Object.fromEntries(
  (
    [
      [
        'Profile & Identity',
        [
          'anon_merge',
          'attribute_change',
          'failed_attribute_change',
          'device_change',
          'profile_merge',
          'profile_create',
          'profile_delete',
        ],
      ],
      [
        'Behavioral Events',
        [
          'event',
          'failed_event',
          'clicked_content',
          'converted_content',
          'viewed_content',
          'page',
          'screen',
          'geofence',
        ],
      ],
      [
        'System Operations',
        [
          'segment_build',
          'skipped_update',
          'failed_batch_update',
          'failed_query_collection',
          'inbound',
          'webhook_event',
        ],
      ],
      ['Suppression Actions', ['spammed_email', 'spammed_action', 'topic_unsubscribed_email']],
    ] as const
  ).flatMap(([group, types]) => types.map((type) => [type, group])),
);

/** `action_channel` (e.g. `delivered_email`) → its action group, or null if not a channel action. */
function channelActionGroup(v: string): string | null {
  const underscore = v.indexOf('_');
  if (underscore <= 0 || underscore >= v.length - 1) return null;
  const action = v.slice(0, underscore);
  if (!CHANNEL_NAMES.has(v.slice(underscore + 1))) return null;
  if (DELIVERY_ACTIONS.has(action)) return 'Delivery Actions';
  if (ENGAGEMENT_ACTIONS.has(action)) return 'Engagement Actions';
  if (SUPPRESSION_ACTIONS.has(action)) return 'Suppression Actions';
  return null;
}

/** Which dropdown group a type belongs to. */
function groupForType(value: string): string {
  const v = value.toLowerCase();

  // Relationship patterns win over the exact-type table (no exact-table type also matches these
  // substrings, so the precedence is only defensive).
  if (v.includes('relationship') || v.includes('object_journeys')) return 'Relationships';
  if (v.startsWith('_o:') || v.startsWith('_r:')) return 'Profile & Identity';

  return GROUP_BY_EXACT_TYPE[v] ?? channelActionGroup(v) ?? 'Other';
}

export interface ActivityTypeOptionsAvailability {
  /** Whether geofencing is available for the account (feature flag + plan feature). */
  isGeofenceAvailable?: boolean;
}

/**
 * Grouped, collapsible options for the Activity Type filter Select — the standard types plus the
 * channel-native types, each bucketed into its category. The Geofence option only appears when
 * geofencing is available for the account, matching journey trigger / segment gating.
 *
 * Deferred: object/relationship wildcard types (`_o:*:` / `_r:*:`) are excluded (they need
 * per-object-type expansion), and channel types aren't yet feature-flag-gated (Builder-plan /
 * disabled-channel hiding).
 */
export function buildActivityTypeOptions(
  terms: ActivityLogTypeTerms,
  { isGeofenceAvailable = false }: ActivityTypeOptionsAvailability = {},
): ActivityTypeOptionGroup[] {
  const overrides: Record<string, string> = {
    profile_create: `${terms.personSingular} Created`,
    profile_delete: `${terms.personSingular} Deleted`,
    profile_merge: `${terms.personPlural} Merged`,
    failed_object_journeys: `Failed Journeys for Object/Relationship ${terms.campaignSingular}`,
  };
  const labelFor = (value: string) =>
    overrides[value] ?? LOG_TYPE_NAMES[value] ?? CHANNEL_LOG_TYPE_NAMES[value] ?? value;

  const values = [...new Set([...Object.keys(LOG_TYPE_NAMES), ...Object.keys(CHANNEL_LOG_TYPE_NAMES)])].filter(
    (value) => !value.startsWith('_o:') && !value.startsWith('_r:'),
  );
  const availableValues = values.filter((value) => value !== LOG_TYPE.Geofence || isGeofenceAvailable);

  const groups = new Map<string, ActivityTypeOption[]>(GROUP_ORDER.map((name) => [name, []]));
  for (const value of availableValues) {
    groups.get(groupForType(value))?.push({ value, label: labelFor(value) });
  }

  return [...groups.entries()]
    .filter(([, options]) => options.length > 0)
    .map(([label, options]) => ({
      label,
      options: options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
      isCollapsible: true,
      defaultIsCollapsed: true,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
